import { acceptPresence, resetPresence, type PresenceSnapshot } from "./presence";
// Live updates over native EventSource('/api/events') (docs/pipeline/V1-CONTRACT.md "Live updates").
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { acceptServerLoan, removeLoan, resetLoanCache } from "./loanCache";
import { qk, refetchEverything } from "./queries";
import type { Loan, Me } from "./types";

export type LiveState = "connecting" | "live" | "reconnecting" | "offline";

export function useLiveEvents(me: Me): LiveState {
  const qc = useQueryClient();
  const [state, setState] = useState<LiveState>("connecting");
  const meRef = useRef(me);
  meRef.current = me;
  const supportKey = JSON.stringify(me.user.supported_ae_ids);

  useEffect(() => {
    let es: EventSource | null = null;
    let closed = false;
    let retryTimer: number | undefined;
    let attempt = 0;
    let summaryTimer: number | undefined;

    const refreshAggregates = () => {
      window.clearTimeout(summaryTimer);
      summaryTimer = window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["summary"] });
        qc.invalidateQueries({ queryKey: ["goals"] });
      }, 600);
    };

    const connect = () => {
      if (closed) return;
      es = new EventSource("/api/events");
      es.onopen = () => {
        attempt = 0;
        setState("live");
        // The server keeps no replay buffer: refetch everything on every (re)connect.
        void refetchEverything(qc);
      };
      es.onerror = () => {
        if (closed) return;
        setState(navigator.onLine ? "reconnecting" : "offline");
        if (es && es.readyState === EventSource.CLOSED) {
          es = null;
          attempt += 1;
          const delay = Math.min(1000 * 2 ** Math.min(attempt, 5), 30_000);
          window.clearTimeout(retryTimer);
          retryTimer = window.setTimeout(connect, delay);
          if (attempt === 2) void qc.invalidateQueries({ queryKey: qk.me });
        }
      };
      es.addEventListener("loan", (e) => {
        try {
          const loan = JSON.parse((e as MessageEvent).data) as Loan;
          acceptServerLoan(qc, loan, meRef.current, true);
          void qc.invalidateQueries({ queryKey: qk.history(loan.id) });
          refreshAggregates();
        } catch {
          /* ignore malformed */
        }
      });
      es.addEventListener("loan_deleted", (e) => {
        try {
          const { id, revision } = JSON.parse((e as MessageEvent).data) as { id: string; revision: number };
          removeLoan(qc, id, revision);
          refreshAggregates();
        } catch {
          /* ignore */
        }
      });
      es.addEventListener("presence", (e) => {
        try { acceptPresence(JSON.parse((e as MessageEvent).data) as PresenceSnapshot); } catch { /* malformed */ }
      });
      es.addEventListener("access_changed", () => {
        es?.close(); es = null;
        resetLoanCache(); resetPresence();
        qc.removeQueries({ predicate: (q) => !["me", "dev"].includes(String(q.queryKey[0])) });
        void qc.invalidateQueries({ queryKey: qk.me });
        window.clearTimeout(retryTimer);
        attempt = 0;
        retryTimer = window.setTimeout(connect, 250);
      });
      es.addEventListener("goal", () => {
        void qc.invalidateQueries({ queryKey: ["goals"] });
        void qc.invalidateQueries({ queryKey: ["summary"] });
      });
    };

    const onOnline = () => {
      if (!es) {
        window.clearTimeout(retryTimer);
        attempt = 0;
        connect();
      }
      setState((s) => (s === "offline" ? "reconnecting" : s));
    };
    const onOffline = () => setState("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    connect();
    return () => {
      closed = true;
      es?.close();
      window.clearTimeout(retryTimer);
      window.clearTimeout(summaryTimer);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [qc, me.user.id, me.user.role, me.user.status, me.user.team_id, supportKey]);

  return state;
}
